import type { AdminReportDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Lightbox } from './Lightbox';
import { MediaGallery } from './MediaGallery';
import { VoiceNotesSection } from './VoiceNotesSection';
import { useAdminReportDetailCache } from '../../../api/admin-reports';
import { publishToast } from '../../../components/feedback';
import { tokens } from '../../../design';

/**
 * Media tab body for the report moderation drawer (AUT-127).
 *
 * Layout (mockup `report-moderation.html`):
 *   - Section title: "Медиа отчёта · N фото · M видео[ · K PDF]" (mono caps).
 *   - 4-col responsive grid of `<MediaTile>` (photo / video / PDF).
 *   - Lightbox opens on a photo / video tile click — keyboard ←/→ cycles,
 *     ESC closes. It pages over the VIEWABLE items only (AUT-429): a PDF
 *     opens in a new tab from its tile and is never a Lightbox stop.
 *   - «Голосовые заметки · N» below the gallery (AUT-429). The empty
 *     state shows only when there is neither media nor a voice note — a
 *     voice-only report renders the voice section alone.
 *
 * 🟠 architect #4 — URL-expiry recovery: any `<img>` / `<video>`
 * `onError` invalidates the detail query so fresh signed URLs replace
 * the expired ones (the parent detail re-fetch swaps `media[].signedUrl`
 * in place). A toast surfaces the recovery so the operator isn't
 * confused by a brief loading flash. A voice note's `<audio>` error does
 * the same, but ONCE per note: a clip that fails again on the re-minted URL
 * (bad file, not an expired link) must not loop invalidations and toasts.
 */
export interface MediaTabProps {
  detail: AdminReportDetailDto;
}

export const MediaTab = ({ detail }: MediaTabProps): ReactElement => {
  const { t } = useTranslation();
  const { invalidateDetail } = useAdminReportDetailCache();
  // Index into `viewable`, not into `detail.media`.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const onMediaError = useCallback((): void => {
    void invalidateDetail(detail.id);
    publishToast({
      severity: 'warning',
      durationMs: 4000,
      message: t('reports.mediaTab.urlExpired'),
    });
  }, [detail.id, invalidateDetail, t]);

  // Once per signed URL, not per note: a re-signed URL of a file that is
  // really gone must not loop, but a note left open past the URL's lifetime
  // must still recover on its next error.
  const failedUrls = useRef(new Set<string>());
  const onAudioError = useCallback(
    (noteId: string): void => {
      const note = detail.voiceNotes.find((n) => n.id === noteId);
      const attempt = `${noteId}:${note?.signedUrl ?? ''}`;
      if (failedUrls.current.has(attempt)) return;
      failedUrls.current.add(attempt);
      onMediaError();
    },
    [detail.voiceNotes, onMediaError],
  );

  const viewable = useMemo(
    () => detail.media.filter((m) => m.mediaType !== 'document'),
    [detail.media],
  );
  const onTileClick = useCallback(
    (mediaId: string): void => {
      const index = viewable.findIndex((m) => m.id === mediaId);
      if (index >= 0) setOpenIndex(index);
    },
    [viewable],
  );

  const photoCount = detail.media.filter((m) => m.mediaType === 'photo').length;
  const videoCount = detail.media.filter((m) => m.mediaType === 'video').length;
  const pdfCount = detail.media.filter((m) => m.mediaType === 'document').length;

  const sectionTitleSx = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.6px',
    color: tokens.text.tertiary,
    mb: 1.5,
  };

  if (detail.media.length === 0 && detail.voiceNotes.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('reports.mediaTab.empty')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {detail.media.length > 0 ? (
        <Box>
          <Typography sx={sectionTitleSx} data-testid="report-detail-media-count">
            {pdfCount > 0
              ? t('reports.mediaTab.sectionTitleWithPdf', { photoCount, videoCount, pdfCount })
              : t('reports.mediaTab.sectionTitle', { photoCount, videoCount })}
          </Typography>
          <MediaGallery media={detail.media} onTileClick={onTileClick} onTileError={onMediaError} />
        </Box>
      ) : null}

      {detail.voiceNotes.length > 0 ? (
        <VoiceNotesSection notes={detail.voiceNotes} onAudioError={onAudioError} />
      ) : null}

      <Lightbox
        open={openIndex !== null}
        media={viewable}
        currentIndex={openIndex ?? 0}
        onClose={() => setOpenIndex(null)}
        onIndexChange={(next) => setOpenIndex(next)}
        onMediaError={onMediaError}
      />
    </Box>
  );
};
